import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { DocumentTextIcon, ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import { useUser } from "@/api/hooks/useUser";
import { Document } from "@/api/rest/models/document";
import { EscalationData } from "@/api/rest/models/escalation";
import {
  type ApiChatMessage,
  askQuestion,
  type ChatStreamEvent,
  ChatStreamEventType,
  type EmitterData,
  EmitterDataTag,
  escalateChat,
  sendEscalation,
} from "@/api/rest/services/chatService.ts";
import { downloadDocument2 } from "@/api/rest/services/documentService";
import DobuLogo from "@/assets/dobu-logo.svg";
import ChatField from "@/components/chat/ChatField.tsx";
import ChatInput from "@/components/chat/ChatInput.tsx";
import { ChatMessage, ChatMessageHolder, ChatMessageType } from "@/components/common/ChatMessage.tsx";
import { DocumentsPreview } from "@/components/household-contents/document/DocumentsPreview.tsx";
import { isMobileDevice } from "@/utils/platform.ts";
import { sendToSentry } from "@/utils/sentry";

import { ChatAskFailed } from "./ChatAskFailed";
import { appendEscalationDeclined, type ChatMessageItem, mapChatHistoryToApi } from "./chatHistory";
import { EscalationForm } from "./EscalationForm";
import { EscalationPrompt } from "./EscalationPrompt";
import { createWarrantyCtaClickHandler, isChatInputDisabled, WarrantyFlowCta } from "./WarrantyFlowCta";

// Define the threshold for showing the warning
const LONG_CHAT_THRESHOLD = 12;

const PROMPT_ESCALATE_TOKEN = "[PROMPT_ESCALATE]";

export default function ChatPopup({
  className,
  householdId,
  documents,
  presetQuestions,
}: {
  className?: string;
  householdId: number;
  documents: Document[];
  presetQuestions?: string[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useUser();
  const [isOpen, setIsOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatMessageItem[]>([]);
  const [currentResponseInProcess, setCurrentResponseInProcess] = useState<string | null>(null);
  const [failedQuestion, setFailedQuestion] = useState<string | null>(null);
  const [showEscalatePrompt, setShowEscalatePrompt] = useState(false);
  const [escalationData, setEscalationData] = useState<EscalationData | null>(null);
  const [showEscalationForm, setShowEscalationForm] = useState(false);
  const [selectedCategoryName, setSelectedCategoryName] = useState<string>("");
  const [currentEscalationTitle, setCurrentEscalationTitle] = useState<string>("");
  const [currentEscalationSummary, setCurrentEscalationSummary] = useState<string>("");
  const [escalationHistory, setEscalationHistory] = useState<ApiChatMessage[] | null>(null);
  const [escalationSendStatus, setEscalationSendStatus] = useState<"success" | "error" | null>(null);
  const [escalationFailed, setEscalationFailed] = useState(false);
  const [createdQuestionId, setCreatedQuestionId] = useState<number>();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImageSrc, setLightboxImageSrc] = useState("");

  const askRequestRef = useRef<AbortController | null>(null);
  // Bumped by each escalation, new question and close; a result for an older number is dropped.
  const escalationRequestRef = useRef(0);
  const isOpenRef = useRef(isOpen); // track the current isOpen state for callbacks

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const handleImageClick = (src: string) => {
    setLightboxImageSrc(src);
    setLightboxOpen(true);
  };

  const sendEscalationMutation = useMutation({
    mutationFn: sendEscalation,
    onSuccess: (question) => {
      setEscalationSendStatus("success");
      setCreatedQuestionId(question.id);
    },
    onError: (error) => {
      setEscalationSendStatus("error");
      console.error("Failed to send escalation:", error);
    },
  });

  // POST that runs an LLM: a mutation, so it never refetches or caches across prompts.
  const escalateMutation = useMutation({
    mutationFn: (history: ApiChatMessage[]) => escalateChat(householdId, history),
  });

  // Forget the escalation of an earlier prompt, and ignore one still in flight.
  const resetEscalation = useCallback(() => {
    escalationRequestRef.current += 1;
    setEscalationData(null);
    setEscalationHistory(null);
    setEscalationFailed(false);
    setShowEscalationForm(false);
    setSelectedCategoryName("");
    setCurrentEscalationTitle("");
    setCurrentEscalationSummary("");
    setEscalationSendStatus(null);
  }, []);

  const scrollRef = useRef<HTMLDivElement>(null);
  const insideInputRef = useRef<HTMLTextAreaElement>(null);

  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  const QUESTION_PRESETS = useMemo(() => {
    if (presetQuestions && presetQuestions.length > 0) {
      return presetQuestions;
    }
    return [t("chat.pin1"), t("chat.pin2"), t("chat.pin3")];
  }, [presetQuestions, t]);

  const responseCallback = useCallback(
    (response: string, isComplete: boolean, tagsFound: EmitterData[], eventsFound: ChatStreamEvent[]) => {
      if (!isOpenRef.current) {
        return;
      }

      if (isComplete) {
        const foundDocuments = tagsFound
          .filter((tag) => tag.tag === EmitterDataTag.DOCUMENTS)
          .flatMap((tag) => tag.data.split(","))
          .map(Number)
          .map((id) => documents.find((doc) => doc.id === id))
          .filter((it): it is Document => it !== undefined);
        const warrantyCta = eventsFound.find(
          (event) => event.type === ChatStreamEventType.SHOW_WARRANTY_FLOW_CTA,
        )?.payload;

        let finalResponse = response;
        if (response.includes(PROMPT_ESCALATE_TOKEN)) {
          finalResponse = response.replace(PROMPT_ESCALATE_TOKEN, "").trim();
          setShowEscalatePrompt(true);
        } else {
          setShowEscalatePrompt(false);
        }

        setChatHistory((oldHistory) => [
          ...oldHistory,
          {
            message: finalResponse,
            isResponse: true,
            linkedDocuments: foundDocuments,
            warrantyCta,
          },
        ]);
        setCurrentResponseInProcess(null);
        askRequestRef.current = null;
        setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight), 100);
      } else {
        setCurrentResponseInProcess(response);
        scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight);
      }
    },
    [documents],
  );

  const submitQuestion = useCallback(
    async (rawQuestion: string, options?: { isNewConversation?: boolean }) => {
      setShowEscalatePrompt(false);
      setFailedQuestion(null);
      resetEscalation();
      const isNewConversationOption = options?.isNewConversation ?? false;

      // 1. Ensure the question is not empty after trimming
      const question = rawQuestion.trim();
      if (question === "") {
        console.warn("submitQuestion called with empty value:", rawQuestion);
        return;
      }

      const newMessage: ChatMessageItem = {
        message: question, // Use the trimmed question
        isResponse: false,
        linkedDocuments: [],
      };

      // 2. Check state *before* potential update to determine if popup was closed
      const wasPopupClosed = !isOpen;

      if (askRequestRef.current) {
        askRequestRef.current.abort();
        askRequestRef.current = null;
      }
      const abortController = new AbortController();
      askRequestRef.current = abortController;
      // A stream that was replaced by a newer question must not write into the chat.
      const onResponse: typeof responseCallback = (...args) => {
        if (askRequestRef.current === abortController) {
          responseCallback(...args);
        }
      };

      try {
        if (wasPopupClosed) {
          // 3. Handle first submission (popup was closed)
          setIsOpen(true); // Open the popup
          setChatHistory([newMessage]); // Start history with the new message
          setCurrentResponseInProcess(""); // Show loading state
          // Call API for the first question with empty history
          await askQuestion(householdId, question, [], onResponse, abortController.signal);
        } else {
          // 4. Handle subsequent submissions (popup was already open)
          const historyToSend = isNewConversationOption
            ? [] // Start new conversation history if explicitly requested
            : mapChatHistoryToApi(chatHistory); // Use existing history

          setCurrentResponseInProcess(""); // Show loading state

          if (isNewConversationOption) {
            setChatHistory([newMessage]); // Replace history if new conversation requested
          } else {
            // Append to existing history
            setChatHistory((oldHistory) => [...oldHistory, newMessage]);
          }

          // Scroll down immediately after adding user message
          setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight), 0);

          // Call API with the question and appropriate history
          await askQuestion(householdId, question, historyToSend, onResponse, abortController.signal);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          // ignore error if request was cancelled
          return;
        }
        // Closing the chat or asking again already took over from this request.
        if (askRequestRef.current !== abortController) {
          return;
        }
        askRequestRef.current = null;
        sendToSentry(error instanceof Error ? error : new Error(String(error)), {
          additionalContext: { component: "ChatPopup" },
        });
        setCurrentResponseInProcess(null);
        setChatHistory((oldHistory) => oldHistory.filter((item) => item !== newMessage));
        setFailedQuestion(question);
      }
    },
    // Dependencies for useCallback
    [householdId, isOpen, responseCallback, chatHistory, resetEscalation],
  );

  const handleEscalateYes = useCallback(() => {
    setShowEscalatePrompt(false);
    setEscalationFailed(false);

    // Back from the form to the same prompt: reuse its escalation.
    if (escalationData) {
      setShowEscalationForm(true);
      return;
    }

    const yesMessageItem: ChatMessageItem = {
      message: t("chat.escalateYes"),
      isResponse: false,
      linkedDocuments: [],
    };
    const historyForApi = mapChatHistoryToApi([...chatHistory, yesMessageItem]);
    const requestId = ++escalationRequestRef.current;
    setEscalationHistory(historyForApi);
    setCurrentResponseInProcess(t("chat.escalationInProgress"));

    escalateMutation.mutate(historyForApi, {
      onSuccess: (data) => {
        if (escalationRequestRef.current !== requestId) {
          return;
        }
        setCurrentResponseInProcess(null);
        setEscalationData(data);
        setSelectedCategoryName(data.escalationCategory);
        setCurrentEscalationTitle(data.title);
        setCurrentEscalationSummary(data.escalationSummary);
        setShowEscalationForm(true);
      },
      onError: (error) => {
        if (escalationRequestRef.current !== requestId) {
          return;
        }
        console.error("Failed to escalate chat:", error);
        setCurrentResponseInProcess(null);
        setEscalationFailed(true);
        setShowEscalatePrompt(true);
      },
    });
  }, [chatHistory, escalateMutation, escalationData, t]);

  const handleEscalateNo = useCallback(() => {
    setShowEscalatePrompt(false);
    setChatHistory((oldHistory) => appendEscalationDeclined(oldHistory, t));
    setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight), 0);
  }, [t]);

  const closeChat = useCallback(() => {
    if (askRequestRef.current) {
      askRequestRef.current.abort();
      askRequestRef.current = null;
    }

    setIsOpen(false);
    setChatHistory([]);
    setCurrentResponseInProcess(null);
    setFailedQuestion(null);
    setShowEscalatePrompt(false);
    resetEscalation();
  }, [resetEscalation]);

  const handleWarrantyCtaClick = useMemo(
    () => createWarrantyCtaClickHandler(closeChat, navigate),
    [closeChat, navigate],
  );

  const handleDialogClose = useCallback(() => {
    if (lightboxOpen) {
      return;
    }
    closeChat();
  }, [lightboxOpen, closeChat]);

  const handleEscalationFormDismiss = useCallback(() => {
    if (escalationSendStatus === "success") {
      closeChat(); // This already resets relevant escalation states
    } else {
      setEscalationSendStatus(null); // Reset status to allow retry or going back
    }
  }, [escalationSendStatus, closeChat]);

  const handleRecipientChange = useCallback((name: string) => {
    setSelectedCategoryName(name);
  }, []);

  const handleTitleChange = useCallback((title: string) => {
    setCurrentEscalationTitle(title);
  }, []);

  const handleSummaryChange = useCallback((summary: string) => {
    setCurrentEscalationSummary(summary);
  }, []);

  const handleSendEscalation = useCallback(() => {
    if (!escalationData || !escalationHistory || !account) {
      return;
    }

    const selectedRecipient = escalationData.escalationOptions.find((opt) => opt.name === selectedCategoryName);

    if (selectedRecipient) {
      sendEscalationMutation.mutate({
        householdId,
        accountId: account.userId,
        history: escalationHistory,
        category: selectedCategoryName,
        title: currentEscalationTitle,
        contentText: currentEscalationSummary,
      });
    } else {
      console.error("Selected recipient not found:", selectedCategoryName);
      // Handle error: perhaps show a message to the user
    }
  }, [
    householdId,
    account,
    escalationData,
    selectedCategoryName,
    currentEscalationTitle,
    currentEscalationSummary,
    escalationHistory,
    sendEscalationMutation,
  ]);

  const handleBackFromEscalationForm = useCallback(() => {
    setShowEscalationForm(false);
    setShowEscalatePrompt(true);
  }, []);

  // Hacky resize listener for mobile
  useEffect(() => {
    if (!isMobileDevice()) {
      return;
    }

    const handleResize = () => {
      if (window.visualViewport) {
        setViewportHeight(window.visualViewport.height);
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", handleResize);
    }

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", handleResize);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => scrollRef.current?.scrollTo(0, scrollRef.current?.scrollHeight), 100);
    }
  }, [chatHistory, currentResponseInProcess, isOpen]);

  useEffect(() => {
    return () => {
      if (askRequestRef.current) {
        askRequestRef.current.abort();
      }
    };
  }, []);

  const showLongChatWarning = chatHistory.length > LONG_CHAT_THRESHOLD;

  return (
    <div className={className}>
      <div className="flex gap-3 sm:gap-2 pb-4 flex-wrap w-full">
        {QUESTION_PRESETS.map((preset, i) => (
          <div
            key={i}
            className="py-2 px-4 sm:py-1 sm:px-3 rounded-full border border-orange-300 bg-white text-sm text-gray-600 hover:bg-orange-100 hover:cursor-pointer"
            onClick={() => submitQuestion(preset, { isNewConversation: true })}
          >
            {preset}
          </div>
        ))}
      </div>
      <ChatField submitQuestion={submitQuestion} />

      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={[{ src: lightboxImageSrc }]}
        plugins={[Zoom]}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
      />

      <Dialog open={isOpen} onClose={handleDialogClose} className="relative z-50" initialFocus={insideInputRef}>
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex w-screen items-center justify-center md:p-4">
          <DialogPanel
            className="mx-auto md:rounded-lg bg-white flex flex-col shadow-xl overflow-clip w-full h-full md:w-[75%] lg:w-[50%] md:h-[80vh] fixed bottom-0 md:bottom-auto"
            style={{
              height: isOpen ? `${isMobileDevice() ? viewportHeight : "80vh"}px` : "auto",
              transition: "height 0.25s ease-out",
            }}
          >
            <DialogTitle className="flex-none text-lg p-4 sm:p-6 font-medium leading-6 text-gray-900 flex flex-row justify-between items-center border-b">
              {showEscalationForm ? (
                t("chat.escalationForm.title")
              ) : (
                <>
                  <SparklesIcon className="w-6 h-6 inline mr-2" />
                  <img className="invert" src={DobuLogo} alt="Logo" width={88} height={16} />
                </>
              )}
              <XCircleIcon
                className="h-6 w-6 text-gray-500 hover:text-dobu-orange-focus focus-visible:text-dobu-orange-focus cursor-pointer"
                onClick={closeChat}
              />
            </DialogTitle>

            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              <div
                className={`transition-transform duration-300 ease-in-out w-full h-full`}
                style={{
                  transform: showEscalationForm ? "translateY(-100%)" : "translateY(0)",
                }}
              >
                {/* Chat View Part */}
                <div className="w-full h-full flex flex-col">
                  <>
                    <ChatMessageHolder
                      scrollRef={scrollRef}
                      className="flex-1 overflow-y-auto p-4" // Added padding
                    >
                      {chatHistory.map((msg, i) => (
                        <ChatMessage
                          key={i}
                          message={msg.message}
                          type={msg.isResponse ? ChatMessageType.RESPONSE : ChatMessageType.QUESTION}
                          onImageClick={handleImageClick}
                        >
                          {msg.linkedDocuments.length > 0 && (
                            <div className="flex flex-row flex-wrap gap-2 mt-2">
                              {msg.linkedDocuments.map((doc) => (
                                <DocumentsPreview
                                  key={doc.id}
                                  className="bg-white hover:bg-orange-100 p-2 rounded text-sm"
                                  document={doc}
                                  onSelect={downloadDocument2}
                                  allowPreview
                                >
                                  <span className="flex items-center gap-1">
                                    <DocumentTextIcon className="h-4 w-4 flex-shrink-0" />
                                    <span className="truncate">{doc.fileName}</span>
                                  </span>
                                </DocumentsPreview>
                              ))}
                            </div>
                          )}
                          {msg.warrantyCta && (
                            <WarrantyFlowCta cta={msg.warrantyCta} onOpenWarrantyFlow={handleWarrantyCtaClick} />
                          )}
                        </ChatMessage>
                      ))}
                      {failedQuestion !== null && (
                        <ChatAskFailed question={failedQuestion} onRetry={() => submitQuestion(failedQuestion)} />
                      )}
                      {showEscalatePrompt && !showEscalationForm && (
                        <EscalationPrompt failed={escalationFailed} onYes={handleEscalateYes} onNo={handleEscalateNo} />
                      )}
                      {currentResponseInProcess !== null && (
                        <ChatMessage
                          message={currentResponseInProcess}
                          type={ChatMessageType.RESPONSE_IN_PROCESS}
                          loadingLabel={t("chat.searchingDocuments")}
                        />
                      )}
                    </ChatMessageHolder>

                    {showLongChatWarning && (
                      <div className="bg-amber-100 border-t border-amber-200 text-amber-800 px-4 py-2 text-sm flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
                        <span>{t("chat.longConversationWarning")}</span>
                      </div>
                    )}

                    <div className="m-2">
                      <ChatInput
                        className="border border-gray-300"
                        name="question"
                        placeholder={t("chat.enterQuestion")}
                        ref={insideInputRef}
                        disabled={isChatInputDisabled({ showEscalatePrompt, showEscalationForm })}
                        sendDisabled={currentResponseInProcess !== null}
                        onSend={(question) => submitQuestion(question, { isNewConversation: false })}
                      />
                    </div>
                  </>
                </div>

                {/* Escalation Form Part */}
                <div className="w-full h-full">
                  {escalationData && (
                    <EscalationForm
                      escalationData={escalationData}
                      selectedCategoryName={selectedCategoryName}
                      title={currentEscalationTitle}
                      summary={currentEscalationSummary}
                      onRecipientChange={handleRecipientChange}
                      onTitleChange={handleTitleChange}
                      onSummaryChange={handleSummaryChange}
                      onSend={handleSendEscalation}
                      onBack={handleBackFromEscalationForm}
                      isLoading={sendEscalationMutation.status === "pending"}
                      sendStatus={escalationSendStatus}
                      onDismissStatus={handleEscalationFormDismiss}
                      createdQuestionId={createdQuestionId}
                    />
                  )}
                </div>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
}
